/**
 * mDNS Redirect Utility (OS v2)
 *
 * Purpose: Best-effort redirect to device's local mDNS name (<serial>.local)
 * instead of publicly-resolvable <guid>.videolink.io domain.
 *
 * Security: mDNS names resolve only on local network, preventing internal IP exposure.
 *
 * @see Technical Design Document Section 5.1 and 7.4
 */

import { logger } from '../logger';

const mdnsLogger = logger.withComponent('osv2-mdns-redirect');

/**
 * mDNS redirect result
 */
export interface MDNSRedirectResult {
  attempted: boolean;
  url?: string;
  success?: boolean;
  error?: string;
  fallbackShown: boolean;
}

/**
 * mDNS redirect options
 */
export interface MDNSRedirectOptions {
  timeout?: number;  // Timeout in milliseconds before showing fallback
  showFallback?: boolean;  // Whether to show fallback message if mDNS fails
  testMode?: boolean;  // Test mode: don't actually redirect
}

/**
 * Default configuration
 */
const DEFAULT_TIMEOUT_MS = 3000;  // 3 seconds
const MDNS_DOMAIN = '.local';
const MDNS_PROTOCOL = 'https://';  // Core UI listens on HTTPS

/**
 * Constructs mDNS URL from serial number
 *
 * Format: http://<serial>.local
 *
 * @param serial - Device serial number
 * @returns mDNS URL
 *
 * @example
 * ```typescript
 * const url = constructMDNSUrl('ABC123');
 * // Returns: "https://abc123.local"
 * ```
 */
export function constructMDNSUrl(serial: string): string {
  // Normalize serial: lowercase, trim
  const normalizedSerial = serial.trim().toLowerCase();

  // Construct mDNS URL
  const url = `${MDNS_PROTOCOL}${normalizedSerial}${MDNS_DOMAIN}`;

  mdnsLogger.debug('Constructed mDNS URL', { serial: normalizedSerial, url });

  return url;
}

/**
 * Checks if mDNS is likely supported in current environment
 *
 * Note: This is best-effort detection. mDNS support depends on:
 * - Network configuration
 * - Router/firewall settings
 * - Operating system
 * - Browser capabilities
 *
 * @returns true if mDNS might be supported
 */
export function isMDNSLikelySupported(): boolean {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return false;
  }

  // Check if we're on localhost/development (mDNS more likely to work)
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.includes('.local');

  // On localhost, mDNS is more likely to work
  if (isLocalhost) {
    mdnsLogger.debug('Running on localhost, mDNS likely supported');
    return true;
  }

  // Otherwise, we can't determine - assume it might work
  mdnsLogger.debug('Environment check inconclusive, assuming mDNS possible');
  return true;
}

/**
 * Attempts to check if mDNS URL is reachable
 *
 * Note: This is best-effort and may be blocked by CORS.
 * In practice, we usually just attempt the redirect and let the browser handle it.
 *
 * @param url - mDNS URL to check
 * @param timeout - Timeout in milliseconds
 * @returns Promise resolving to true if reachable
 */
export async function checkMDNSReachability(
  url: string,
  timeout: number = DEFAULT_TIMEOUT_MS
): Promise<boolean> {
  try {
    mdnsLogger.debug('Checking mDNS reachability', { url, timeout });

    // Attempt HEAD request with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      await fetch(url, {
        method: 'HEAD',
        signal: controller.signal,
        mode: 'no-cors',  // Avoid CORS issues
      });

      clearTimeout(timeoutId);

      // With no-cors, we can't check status, but if it doesn't throw, it's reachable
      mdnsLogger.info('mDNS URL appears reachable', { url });
      return true;

    } catch (fetchError) {
      clearTimeout(timeoutId);

      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        mdnsLogger.warn('mDNS check timed out', { url, timeout });
      } else {
        mdnsLogger.warn('mDNS check failed', { url, error: fetchError });
      }

      return false;
    }

  } catch (error) {
    mdnsLogger.error('mDNS reachability check error', { url, error });
    return false;
  }
}

/**
 * Attempts best-effort mDNS redirect
 *
 * This is the main OS v2 redirect function that replaces videolink.io redirects.
 *
 * Behavior:
 * 1. Constructs <serial>.local URL
 * 2. Optionally checks reachability (if timeout > 0)
 * 3. Attempts redirect (best-effort)
 * 4. If redirect fails or times out, caller should show fallback message
 *
 * @param serial - Device serial number
 * @param options - Redirect options
 * @returns Promise resolving to redirect result
 *
 * @example
 * ```typescript
 * const result = await attemptMDNSRedirect('ABC123', { timeout: 3000 });
 * if (result.fallbackShown) {
 *   // Show success message with support contact
 * }
 * ```
 */
export async function attemptMDNSRedirect(
  serial: string,
  options: MDNSRedirectOptions = {}
): Promise<MDNSRedirectResult> {
  const {
    timeout = DEFAULT_TIMEOUT_MS,
    showFallback = true,
    testMode = false,
  } = options;

  mdnsLogger.info('Attempting mDNS redirect', { serial, options });

  try {
    // Step 1: Construct mDNS URL
    const mdnsUrl = constructMDNSUrl(serial);

    // Step 2: Check if mDNS likely supported (informational only)
    const likelySupported = isMDNSLikelySupported();
    if (!likelySupported) {
      mdnsLogger.warn('mDNS may not be supported in this environment');
    }

    // Step 3: Optionally check reachability
    let isReachable = true;  // Assume reachable unless check fails
    if (timeout > 0) {
      isReachable = await checkMDNSReachability(mdnsUrl, timeout);
    }

    // Step 4: Attempt redirect (unless test mode)
    if (!testMode) {
      if (isReachable) {
        mdnsLogger.info('Redirecting to mDNS URL', { mdnsUrl });
        window.location.href = mdnsUrl;

        return {
          attempted: true,
          url: mdnsUrl,
          success: true,
          fallbackShown: false,
        };
      } else {
        mdnsLogger.warn('mDNS URL not reachable, showing fallback', { mdnsUrl });

        return {
          attempted: true,
          url: mdnsUrl,
          success: false,
          fallbackShown: showFallback,
        };
      }
    } else {
      // Test mode: don't actually redirect
      mdnsLogger.debug('Test mode: redirect not performed', { mdnsUrl });

      return {
        attempted: true,
        url: mdnsUrl,
        success: isReachable,
        fallbackShown: !isReachable && showFallback,
      };
    }

  } catch (error) {
    mdnsLogger.error('mDNS redirect error', { serial, error });

    return {
      attempted: true,
      error: error instanceof Error ? error.message : 'Unknown error',
      fallbackShown: showFallback,
    };
  }
}

/**
 * Validates mDNS URL format
 *
 * @param url - URL to validate
 * @returns true if valid mDNS URL format
 */
export function isValidMDNSUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname.endsWith(MDNS_DOMAIN) &&
           (parsed.protocol === 'http:' || parsed.protocol === 'https:');
  } catch {
    return false;
  }
}

/**
 * Formats fallback message for when mDNS redirect fails
 *
 * This message is shown to the user when mDNS doesn't resolve.
 *
 * @param serial - Device serial number
 * @param supportPhone - Support phone number
 * @returns Formatted fallback message
 */
export function getFallbackMessage(serial: string, supportPhone: string): string {
  return `Your Fusus Core (Serial: ${serial}) has been configured successfully.

If the device is not accessible on your network, please ensure:
- The Core is powered on and connected to Ethernet
- Your device is on the same network as the Core
- mDNS (Bonjour) is not blocked by your firewall

Need help? Contact support at ${supportPhone}`;
}

/**
 * Analytics helper: Track mDNS redirect attempts
 *
 * @param serial - Device serial number
 * @param result - Redirect result
 */
export function trackMDNSRedirect(serial: string, result: MDNSRedirectResult): void {
  mdnsLogger.info('mDNS redirect completed', {
    serial,
    attempted: result.attempted,
    success: result.success,
    fallbackShown: result.fallbackShown,
    url: result.url,
  });

  // TODO: Add Mixpanel/analytics tracking here
  // mixpanelService.track('OSv2_mDNS_Redirect_Attempted', { ... });
}
